import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, Loader2, CheckCircle2, Circle, Clock, AlertCircle, RotateCcw, MailPlus,
  Users, Home, FileText, FileSignature, Star, ScanFace,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { invitationsAPI } from '../lib/invitationsAPI';
import { Send, XCircle } from 'lucide-react';
import { onboardingReviewAPI } from '../lib/onboardingReviewAPI';
import { SECTION_STATUS_META } from '../lib/onboardingAPI';
import { LETTER_STATUS_META, fmtMoney } from '../lib/servicesEngagementAPI';
import { ClearancePanel } from '../components/clearance/ClearancePanel';
import { ClientNominationTab } from '../components/nomination/ClientNominationTab';
import { MemberInviteDialog } from '../components/invitations/MemberInviteDialog';
import { IdentityReviewPanel } from '../components/review/IdentityReviewPanel';
import { ApproveDialog } from '../components/review/ApproveDialog';

const sIcon = (s) => ({
  completed: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  in_progress: <Clock className="w-4 h-4 text-amber-500" />,
  needs_review: <AlertCircle className="w-4 h-4 text-orange-500" />,
}[s] || <Circle className="w-4 h-4 text-muted-foreground/50" />);

// Didit KYC status → badge meta for the accountant view.
const IDV_META = {
  'Approved':    { label: 'Verified', color: 'bg-green-100 text-green-800' },
  'In Review':   { label: 'In review', color: 'bg-amber-100 text-amber-800' },
  'In Progress': { label: 'In progress', color: 'bg-blue-100 text-blue-800' },
  'Awaiting User': { label: 'Awaiting user', color: 'bg-amber-100 text-amber-800' },
  'Declined':    { label: 'Declined', color: 'bg-red-100 text-red-800' },
  'Expired':     { label: 'Expired', color: 'bg-muted text-muted-foreground' },
  'Kyc Expired': { label: 'Expired', color: 'bg-muted text-muted-foreground' },
  'Abandoned':   { label: 'Not finished', color: 'bg-muted text-muted-foreground' },
  'Not Started': { label: 'Not started', color: 'bg-muted text-muted-foreground' },
};

// Pretty-print a single scalar value.
const fmtVal = (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v).slice(0, 120));

// Render one flat object as a grid of label: value pairs.
function FieldGrid({ obj }) {
  const entries = Object.entries(obj).filter(([, v]) =>
    v !== null && v !== '' && typeof v !== 'object');
  if (entries.length === 0) return <span className="text-xs text-muted-foreground/70">—</span>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="text-xs">
          <span className="text-muted-foreground/70">{k.replace(/_/g, ' ')}: </span>
          <span className="text-foreground/90">{fmtVal(v)}</span>
        </div>
      ))}
    </div>
  );
}

// Compact render of a section's saved answers. Scalars show as label: value pairs;
// arrays of objects (investments, foreign income, related entities) expand into
// numbered cards so the accountant sees every item's detail inline.
function ResponseView({ data }) {
  if (!data || Object.keys(data).length === 0) return <p className="text-xs text-muted-foreground/70">No data captured.</p>;

  const scalarEntries = Object.entries(data).filter(([, v]) =>
    v !== null && v !== '' && typeof v !== 'object');
  const arrayEntries = Object.entries(data).filter(([, v]) =>
    Array.isArray(v) && v.length > 0);

  if (scalarEntries.length === 0 && arrayEntries.length === 0) {
    return <p className="text-xs text-muted-foreground/70">No data captured.</p>;
  }

  return (
    <div className="space-y-3">
      {scalarEntries.length > 0 && <FieldGrid obj={Object.fromEntries(scalarEntries)} />}
      {arrayEntries.map(([k, arr]) => (
        <div key={k} className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground">{k.replace(/_/g, ' ')} ({arr.length})</p>
          {arr.map((item, i) => (
            <div key={i} className="border rounded-md p-2 bg-muted/50">
              {item && typeof item === 'object'
                ? <FieldGrid obj={item} />
                : <span className="text-xs text-foreground/90">{fmtVal(item)}</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function OnboardingReviewDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await onboardingReviewAPI.bundle(clientId); setData(res.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to load review'); navigate('/new-clients'); }
    finally { setLoading(false); }
  }, [clientId, navigate]);

  useEffect(() => { load(); }, [load]);

  const [approveOpen, setApproveOpen] = useState(false);
  const approve = async (body) => {
    const reapproving = data?.client?.status === 'active';
    setBusy(true);
    try {
      const { data: r } = await onboardingReviewAPI.approve(clientId, body);
      const inv = r.invite?.result === 'invited' ? ' · portal invite sent' : r.invite?.reason ? ` · invite not sent: ${r.invite.reason}` : '';
      toast.success((reapproving ? 'Re-submitted changes approved' : 'Client approved & created') + inv);
      setApproveOpen(false);
      await load();
    }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === 'string' ? d : d?.message || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const reopen = async (key) => {
    try { await onboardingReviewAPI.reopenSection(clientId, key); toast.success('Section reopened — client can update it'); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const [inviteMember, setInviteMember] = useState(null);
  const [role, setRole] = useState();
  useEffect(() => { api.get('/api/me').then((r) => setRole(r.data.active_membership?.role)).catch(() => {}); }, []);
  const canManage = ['owner', 'admin', 'accountant', 'bookkeeper', 'staff'].includes(role);
  const inviteAct = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Action failed'); }
  };

  if (loading || !data) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  const { client, group, members, checklist, sections, rentals, documents, selected_services, engagement_letter, identity_verification } = data;
  const approved = client.status === 'active';
  // After approval, re-enable approve only when the client has re-submitted changes that need re-review.
  const needsReapproval = approved && client.updated_after_submit;
  const inv = data.invitation;
  const invLive = inv && !['cancelled', 'client_created'].includes(inv.status) && !approved;
  const selfRegistered = !inv && !['spouse_onboarding', 'related_entity_onboarding'].includes(client.created_by);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="new-client-detail">
      <Button variant="ghost" onClick={() => navigate('/new-clients')} className="text-muted-foreground -ml-2" data-testid="nc-detail-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> New Clients
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{client.display_name}</h1>
            {(identity_verification?.didit_status === 'Approved' || ['verified', 'verified_manual'].includes(identity_verification?.id_verification_status)) && (
              <Badge className="bg-green-100 text-green-800 flex items-center gap-1" data-testid="header-id-verified">
                <ScanFace className="w-3 h-3" /> {identity_verification?.id_verification_status === 'verified_manual' ? 'ID manually verified' : 'ID Verified'}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm capitalize">{(client.entity_type || '').replace('_', ' ')} · {group?.group_name || 'No group'} · status {client.status}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {canManage && invLive && <>
            <Button variant="outline" onClick={() => inviteAct(() => invitationsAPI.resend(inv.id), `Invitation resent to ${inv.email}`)} data-testid="nc-detail-resend"><Send className="w-4 h-4 mr-1" /> Resend invite</Button>
            <Button variant="outline" onClick={() => inviteAct(() => invitationsAPI.cancel(inv.id), 'Invitation cancelled')} data-testid="nc-detail-cancel"><XCircle className="w-4 h-4 mr-1" /> Cancel invite</Button>
          </>}
          <Button variant="outline" onClick={() => navigate(`/clients/${clientId}/engagement`)}><FileSignature className="w-4 h-4 mr-1" /> Services &amp; Engagement</Button>
          <Button onClick={() => (needsReapproval ? approve({}) : setApproveOpen(true))} disabled={busy || (approved && !needsReapproval)} data-testid="approve-client">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {needsReapproval ? 'Re-approve changes' : approved ? 'Approved' : 'Approve & create client'}
          </Button>
        </div>
      </div>

      {/* Re-submission alert — client edited details after submitting */}
      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen} client={client} checklist={checklist} identity={identity_verification}
        portalLogin={data.portal_login} selfRegistered={selfRegistered} busy={busy} onConfirm={approve} />
      {client.updated_after_submit && (
        <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="resubmitted-alert">
          <RotateCcw className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-orange-800">Client re-submitted after changes</p>
            <p className="text-sm text-orange-700">This client edited their details after submitting. The sections marked <strong>Re-submitted</strong> below have changed — please re-check them before approving.</p>
          </div>
        </div>
      )}

      {/* Progress + missing */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-foreground/90">Onboarding progress</span>
            <span className="text-sm font-semibold text-foreground">{checklist.completion_pct}%</span>
          </div>
          <Progress value={checklist.completion_pct} className="h-2" />
          <p className="text-xs text-muted-foreground mt-2">{checklist.completed_sections}/{checklist.total_sections} sections · submitted: {client.onboarding_submitted ? 'yes' : 'no'}</p>
          {checklist.required_incomplete?.length > 0 && (
            <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Missing required sections: {checklist.required_incomplete.join(', ')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group members */}
      {members?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-foreground" /> Client Group</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 border rounded-lg p-2.5">
                {m.id === group?.primary_client_id && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
                <div className="flex-1">
                  <button className="font-medium text-foreground hover:underline" onClick={() => navigate(`/clients/${m.id}`)}>{m.display_name}</button>
                  <span className="ml-2 text-xs text-muted-foreground/70">{(m.relationship_type || 'related').replace('_', ' ')}</span>
                </div>
                {m.id !== clientId && (
                  <Button size="sm" variant="ghost" onClick={() => setInviteMember(m)} title="Send registration link" data-testid={`member-invite-btn-${m.id}`}>
                    <MailPlus className="w-4 h-4 mr-1 text-blue-600" /> Send link
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {inviteMember && <MemberInviteDialog clientId={clientId} member={inviteMember} onClose={() => setInviteMember(null)} />}

      {/* Identity verification (Didit KYC, mandatory) */}
      {identity_verification && <IdentityReviewPanel clientId={clientId} idv={identity_verification} onChanged={load} />}

      {/* Sections review */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Onboarding Sections</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {sections.filter((s) => s.applicable).map((s) => {
            const meta = SECTION_STATUS_META[s.status] || SECTION_STATUS_META.not_started;
            return (
              <div key={s.key} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-2 font-medium text-foreground">{sIcon(s.status)} {s.title}</span>
                  <span className="flex items-center gap-2">
                    <Badge className={meta.color}>{meta.label}</Badge>
                    {s.edited_after_submit && (
                      <Badge className="bg-orange-100 text-orange-800 flex items-center gap-1" data-testid={`resubmitted-${s.key}`}>
                        <RotateCcw className="w-3 h-3" /> Re-submitted
                      </Badge>
                    )}
                    {!s.terminal && (
                      <Button size="sm" variant="ghost" onClick={() => reopen(s.key)} title="Request more info">
                        <RotateCcw className="w-4 h-4 text-orange-500" />
                      </Button>
                    )}
                  </span>
                </div>
                {!s.terminal && <ResponseView data={s.response_data} />}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Rentals */}
      {rentals?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Home className="w-5 h-5 text-foreground" /> Rental Properties ({rentals.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rentals.map((p) => (
              <div key={p.id} className="text-sm border rounded-lg p-2.5">
                <span className="font-medium text-foreground">{p.property_nickname || p.property_address}</span>
                <span className="text-xs text-muted-foreground/70 ml-2">{p.ownership_percentage}% · {p.property_type}{p.sold_during_year ? ' · sold' : ''}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documents (onboarding upload step is off: only previously uploaded files are listed) */}
      {documents.length > 0 && <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-foreground" /> Documents</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {documents.map((d) => (
            <div key={d.key} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
              <span className="text-foreground/90">{d.label}{d.required && <span className="text-xs text-red-500 ml-1">required</span>}</span>
              <Badge className={d.files.length ? 'bg-blue-100 text-blue-800' : d.required ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'}>
                {d.files.length ? `${d.files.length} uploaded` : (d.required ? 'Required' : 'Optional')}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>}

      {/* Services & engagement */}
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileSignature className="w-5 h-5 text-foreground" /> Services &amp; Engagement</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {selected_services?.length > 0 ? (
            <div className="text-sm text-muted-foreground">{selected_services.length} service(s) selected.</div>
          ) : <div className="text-sm text-muted-foreground/70">No services selected yet.</div>}
          {engagement_letter ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{engagement_letter.engagement_letter_number}</span>
              <Badge className={(LETTER_STATUS_META[engagement_letter.status] || {}).color}>{(LETTER_STATUS_META[engagement_letter.status] || {}).label || engagement_letter.status}</Badge>
              {engagement_letter.fee_summary && <span className="text-muted-foreground">{fmtMoney(engagement_letter.fee_summary.total)}</span>}
            </div>
          ) : <div className="text-sm text-muted-foreground/70">No engagement letter generated.</div>}
          <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${clientId}/engagement`)}>Manage services &amp; letter</Button>
        </CardContent>
      </Card>

      {/* Ethical clearance — letter to the outgoing accountant */}
      <Card id="ato-nomination" data-testid="nc-detail-nomination">
        <CardHeader><CardTitle className="text-lg">ATO nomination</CardTitle></CardHeader>
        <CardContent><ClientNominationTab clientId={clientId} /></CardContent>
      </Card>
      <ClearancePanel clientId={clientId} />
    </div>
  );
}
